<template lang="pug">
.pen-canvas(:class="`pen-canvas--${mode}`", ref="canvasArea")
  // ベースキャンバス: 既存ストロークをレンダリング (最終描画用)
  canvas-drawing-base.pen-canvas__canvas(
    ref="canvas",
  )

  // オーバーレイキャンバス: ドラッグ（リアルタイム描画）処理 (イベント処理用)
  canvas-drawing-base.pen-canvas__canvas.overlay-canvas(
    ref="overlayCanvas",
    @dragStart="dragStart",
    @dragEnd="dragEnd",
    @dragging="dragging",
    :data-activated="!!pen",
  )
</template>

<script lang="ts">
import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator'
import CanvasDrawingBase from '@/components/atoms/CanvasDrawingBase.vue'
import { DrawSettings } from '@/models/drawSettings'
import { DrawingDataType, Stroke, Point, PenType } from '@/models/drawingDataModel'
import { CanvasUtils } from '@/utils/CanvasUtils'
import { PenSettings } from '@/const/PenSettings'

/**
 * 画面メモ用キャンバスタグ
 */
@Component({
  components: {
    CanvasDrawingBase,
  },
})
export default class PenCanvas extends Vue {
  // ペン情報(nullの場合はcanvasを非アクティブ化する)
  @Prop()
  pen!: DrawSettings.PenAsset | null

  // 描画データ（ストローク情報）
  @Prop()
  drawingData?: DrawingDataType

  @Prop({ default: 'horizontal' })
  mode!: 'horizontal' | 'vertical'

  // canvas表示エリアアクセス用
  @Ref()
  public canvasArea!: HTMLElement
  // canvasコンポーネントアクセス用
  @Ref()
  public canvas!: CanvasDrawingBase

  // オーバーレイキャンバスコンポーネントアクセス用
  @Ref()
  public overlayCanvas!: CanvasDrawingBase

  // 描画中のストローク情報
  private currentStroke: Stroke | null = null
  // 現在のストローク座標情報
  private currentPoints: Point[] = []
  // すべてのストロークを保持する配列
  private strokes: Stroke[] = []

  // サイズ変更時のイベントハンドラ
  private resizeObserver: ResizeObserver | null = null

  /**
   * ペンの切り替え時イベント
   */
  @Watch('pen')
  private changePen() {
    // ペンが未指定になった場合は終了
    if (!this.pen || !this.overlayCanvas) return
    const penSettings = this.getPenSettings(this.pen.name as PenType)
    // 描画contextにペンの情報を設定する
    this.applyPenSettings(penSettings, this.overlayCanvas)
  }

  /**
   * 描画データの変更時イベント
   */
  @Watch('drawingData', { deep: true })
  private onDrawingDataChanged() {
    if (this.drawingData) {
      this.strokes = [...this.drawingData.strokes]
      this.redrawCanvas()
    }
  }

  /**
   * 初期処理
   */
  private mounted() {
    // ウィンドウサイズ変更を監視
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.adjustCanvasSize()
      })
    })
    if (this.canvasArea) this.resizeObserver.observe(this.canvasArea)
    this.adjustCanvasSize()

    // 初期描画データがある場合は復元
    if (this.drawingData) {
      this.strokes = [...this.drawingData.strokes]
    }
  }

  /**
   * 離脱時処理
   */
  private beforeDestroy() {
    // イベントの削除
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  /**
   * キャンバスサイズの調整が必要な場合は調整する
   */
  private adjustCanvasSize() {
    if (this.mode === 'horizontal') return

    const area = this.canvasArea
    const base = this.canvas?.canvasElement
    const overlay = this.overlayCanvas?.canvasElement
    if (!area || !base || !overlay) return

    // ターゲットサイズはラッパーエレメント(canvasArea)の実際の大きさ
    const targetWidth = area.scrollWidth
    const targetHeight = area.scrollHeight

    if ((base.width !== targetWidth || base.height !== targetHeight) && targetWidth > 0 && targetHeight > 0) {
      const oldWidth = base.width
      const oldHeight = base.height

      // ベースおよびオーバーレイキャンバスサイズ設定
      this.canvas.setCanvasSize(targetWidth, targetHeight)
      this.overlayCanvas.setCanvasSize(targetWidth, targetHeight)

      // キャンバスサイズが変更された場合、ストロークの座標をスケールする
      if (oldWidth > 0 && oldHeight > 0) {
        const scaleX = targetWidth / oldWidth
        const scaleY = targetHeight / oldHeight
        this.scaleStrokes(scaleX, scaleY)
      } else {
        this.redrawCanvas()
      }

      // オーバーレイキャンバスをクリア
      this.overlayCanvas.context.clearRect(0, 0, targetWidth, targetHeight)
    }
  }

  /**
   * ストロークの座標をスケールする
   */
  private scaleStrokes(scaleX: number, scaleY: number) {
    this.strokes = this.strokes.map((stroke) => ({
      ...stroke,
      points: stroke.points.map((point) =>
        this.roundPoint({
          x: point.x * scaleX,
          y: point.y * scaleY,
        })
      ),
    }))
    this.redrawCanvas()
  }

  /**
   * キャンバス全体を再描画する (ベースキャンバスのみ)
   */
  private redrawCanvas() {
    const c = this.canvas?.canvasElement
    const ctx = this.canvas?.context
    if (!c || !ctx) return

    // キャンバスをクリア
    ctx.clearRect(0, 0, c.width, c.height)

    // 保存されているストロークを順番に描画
    for (const stroke of this.strokes) this.drawStroke(stroke, this.canvas)
  }

  /**
   * ストロークを描画する
   */
  private drawStroke(stroke: Stroke, targetCanvas: CanvasDrawingBase) {
    if (!targetCanvas || stroke.points.length === 0) return
    const ctx = targetCanvas.context

    // コンテキスト状態保存
    ctx.save()

    const penSettings = this.getPenSettings(stroke.pen)

    // ストロークのペン設定を適用
    this.applyPenSettings(penSettings, targetCanvas)

    ctx.beginPath()
    stroke.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)))
    ctx.stroke()

    // 元の設定を復元
    ctx.restore()
  }

  /**
   * ドラッグ(描画)開始
   */
  private dragStart(e: TouchEvent | MouseEvent) {
    // スクロール抑制
    e.preventDefault()

    if (!this.overlayCanvas || !this.overlayCanvas.canvasElement || !this.pen) return

    // 座標計算
    const point = this.roundPoint(CanvasUtils.caliculatePosition(e, this.overlayCanvas.canvasElement))

    // 新しいストロークを開始
    this.currentPoints = [point]
    this.currentStroke = {
      points: this.currentPoints,
      pen: this.pen.name as PenType,
    }
  }

  /**
   * ドラッグ(描画)中
   */
  private dragging(e: TouchEvent | MouseEvent) {
    // スクロール抑制
    e.preventDefault()

    if (!this.currentStroke || !this.overlayCanvas || !this.overlayCanvas.canvasElement) return

    // 座標計算
    const point = this.roundPoint(CanvasUtils.caliculatePosition(e, this.overlayCanvas.canvasElement))

    // 現在のストロークに座標を追加
    this.currentPoints.push(point)

    const ctx = this.overlayCanvas.context
    ctx.clearRect(0, 0, this.overlayCanvas.canvasElement.width, this.overlayCanvas.canvasElement.height)
    this.drawStroke(this.currentStroke, this.overlayCanvas)
  }

  /**
   * ドラッグ(描画)終了
   */
  private dragEnd(e: TouchEvent | MouseEvent) {
    e.preventDefault()
    if (!this.currentStroke || !this.overlayCanvas) return

    // 消しゴム処理
    if (this.currentStroke.pen === 'eraser') {
      this.strokes = this.calculateStrokes()
    } else {
      this.strokes.push({ ...this.currentStroke })
    }

    // キャンバスを再描画
    this.redrawCanvas()

    if (!this.canvas || !this.canvas.canvasElement) return
    const c = this.canvas.canvasElement

    // 描画データを生成してemit
    const drawingData: DrawingDataType = {
      strokes: [...this.strokes],
      canvasSize: {
        width: c.width,
        height: c.height,
      },
    }

    // キャンバス上の画像のエンコード情報とベクター情報をemit
    this.$emit('draw-end', {
      encodedImage: c.toDataURL(),
      drawingData,
    })

    this.overlayCanvas.context.clearRect(0, 0, c.width, c.height)
    this.currentStroke = null
    this.currentPoints = []
  }

  /**
   * キャンバスのコンテキストにペン設定を適用する
   */
  private applyPenSettings(pen: DrawSettings.PenAsset, targetCanvas: CanvasDrawingBase) {
    const ctx = targetCanvas.context

    // 基本設定
    ctx.lineCap = pen.lineCap
    ctx.lineWidth = pen.lineWidth
    ctx.globalAlpha = pen.alpha || PenSettings.ALPHA_100

    // 消しゴムの場合は白で表示 (オーバーレイ用)
    if (pen.name === 'eraser') {
      ctx.strokeStyle = 'rgba(255,255,255,1.0)'
      // ベースキャンバスへの描画（最終描画）では'destination-out'を使用
      if (targetCanvas === this.canvas) {
        ctx.globalCompositeOperation = 'destination-out'
      } else {
        // オーバーレイキャンバスへの描画（リアルタイム描画）では 'source-over'
        ctx.globalCompositeOperation = 'source-over'
      }
    } else {
      ctx.strokeStyle = pen.color || PenSettings.COLOR_BLACK
      ctx.globalCompositeOperation = pen.operation as CanvasRenderingContext2D['globalCompositeOperation']
    }
  }

  private getPenSettings(penType: PenType): DrawSettings.PenAsset {
    switch (penType) {
      case 'black':
        return PenSettings.PEN_BLACK
      case 'red':
        return PenSettings.PEN_RED
      case 'highlight':
        return PenSettings.PEN_HIGHLIGHT
      case 'eraser':
        return PenSettings.ERASER
      default:
        return PenSettings.PEN_BLACK
    }
  }

  /**
   * 座標値を小数点以下第一位まで四捨五入する
   */
  private roundCoordinate(value: number): number {
    return Math.round(value * 10) / 10
  }

  /**
   * 座標オブジェクトの値を四捨五入する
   */
  private roundPoint(point: Point): Point {
    return {
      x: this.roundCoordinate(point.x),
      y: this.roundCoordinate(point.y),
    }
  }

  /**
   * 現在ドラッグ中のストローク（主に消しゴム）を基準として
   * 既存ストローク配列を計算して返還
   */
  private calculateStrokes(): Stroke[] {
    if (!this.currentStroke) return this.strokes
    if (this.currentStroke.pen !== 'eraser') return [...this.strokes, { ...this.currentStroke }]

    const eraserBounds = this.getBounds(this.currentStroke.points)
    const eraserWidth = PenSettings.ERASER.lineWidth / 2

    return this.strokes.filter((stroke) => {
      const strokeBounds = this.getBounds(stroke.points)

      // バウンディングボックス段階で早期除外
      if (!this.boundsIntersect(eraserBounds, strokeBounds, eraserWidth)) {
        return true
      }

      // 詳細な線分間距離計算
      return !this.strokesIntersect(stroke, this.currentStroke!, eraserWidth)
    })
  }

  // 境界ボックス計算
  private getBounds(points: Point[]) {
    let minX = Infinity,
      maxX = -Infinity
    let minY = Infinity,
      maxY = -Infinity

    for (const p of points) {
      if (p.x < minX) minX = p.x
      if (p.x > maxX) maxX = p.x
      if (p.y < minY) minY = p.y
      if (p.y > maxY) maxY = p.y
    }

    return { minX, maxX, minY, maxY }
  }

  // 境界ボックスの交差判定
  private boundsIntersect(
    b1: ReturnType<typeof this.getBounds>,
    b2: ReturnType<typeof this.getBounds>,
    margin: number
  ) {
    return !(
      b1.maxX + margin < b2.minX - margin ||
      b2.maxX + margin < b1.minX - margin ||
      b1.maxY + margin < b2.minY - margin ||
      b2.maxY + margin < b1.minY - margin
    )
  }

  // 詳細な交差判定
  private strokesIntersect(stroke1: Stroke, stroke2: Stroke, threshold: number) {
    for (let i = 1; i < stroke1.points.length; i++) {
      for (let j = 1; j < stroke2.points.length; j++) {
        if (
          this.segmentDistance(stroke1.points[i - 1], stroke1.points[i], stroke2.points[j - 1], stroke2.points[j]) <
          threshold
        ) {
          return true
        }
      }
    }
    return false
  }

  // 両線分間の最短距離
  private segmentDistance(p1: Point, p2: Point, p3: Point, p4: Point): number {
    const dot = (a: Point, b: Point) => a.x * b.x + a.y * b.y
    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))
    const EPS = 1e-6
    const u = { x: p2.x - p1.x, y: p2.y - p1.y }
    const v = { x: p4.x - p3.x, y: p4.y - p3.y }
    const w = { x: p1.x - p3.x, y: p1.y - p3.y }
    const uu = dot(u, u)
    const vv = dot(v, v)
    const uv = dot(u, v)
    const uw = dot(u, w)
    const vw = dot(v, w)

    const D = uu * vv - uv * uv

    let s, t
    if (D < EPS) {
      s = 0
      t = clamp(vw / vv, 0, 1)
    } else {
      s = clamp((uv * vw - vv * uw) / D, 0, 1)
      t = clamp((uu * vw - uv * uw) / D, 0, 1)
    }
    const cx1 = p1.x + s * u.x
    const cy1 = p1.y + s * u.y
    const cx2 = p3.x + t * v.x
    const cy2 = p3.y + t * v.y
    const dx = cx1 - cx2
    const dy = cy1 - cy2

    return Math.sqrt(dx * dx + dy * dy)
  }

  // 描画データを復元する
  public restoreDrawingData(drawingData: DrawingDataType | null) {
    if (!this.canvas || !this.canvas.canvasElement) {
      return
    }
    if (!drawingData) {
      this.strokes = []
      this.redrawCanvas()
      return
    }

    if (!drawingData.canvasSize) {
      this.strokes = [...drawingData.strokes]
      this.redrawCanvas()
      return
    }

    const scaleX = this.canvas.canvasElement.width / drawingData.canvasSize.width
    const scaleY = this.canvas.canvasElement.height / drawingData.canvasSize.height

    this.strokes = drawingData.strokes.map((stroke) => ({
      ...stroke,
      points: stroke.points.map((point) =>
        this.roundPoint({
          x: point.x * scaleX,
          y: point.y * scaleY,
        })
      ),
    }))

    this.redrawCanvas()
  }
}
</script>

<style lang="stylus" scoped>
.pen-canvas
  width 100%
  height 100%
  position relative
  pointer-events none

  &__canvas
    display block
    position absolute
    top 0
    left 0
  &--horizontal
    & > .pen-canvas__canvas
      width 100%
      height auto

  &--vertical
    & > .pen-canvas__canvas
      height 100%
      width 100% // 縦書きモードでも幅を合わせる

  .overlay-canvas
    &[data-activated='true']
      // penがアクティブな場合のみイベントを受け付ける
      pointer-events auto
</style>
